<template>
	<div
		v-if="setup.discussionService"
		id="discussions"
		class="shared-discussions">
		<div
			v-if="setup.discussionService.openedDiscussionIds.length || setup.displayDiscussionList"
			:ref="setup._discussionsList"
			class="shared-discussions__opened"
			:class="{ 'shared-discussions__opened--list-closed': !setup.displayDiscussionList }">
			<o-card
				v-if="setup.displayDiscussionList"
				class="discussions-list-card">
				<div class="flex ai-c jc-sb mt-sm ph-sm">
					<h4 class="ma-0">Discussions</h4>
					<span
						class="shared-discussions__list-close"
						@click="setup.hide()"/>
				</div>

				<o-chat-discussion-list
					:chat="setup.discussionService.chat"
					@select-discussion="setup.discussionService.open($event)">
					<template #append-discussion-list-header>
						<o-toggle
							v-model="setup.discussionService.unreadOnly"
							label="Uniquement les non-lues"
							reverse/>
					</template>

					<template #append-discussion-item="{ discussion }">
						<div class="flex g-xs mt-xs">
							<o-label
								v-if="setup.self.isAgency() || setup.self.isClient()"
								outline
								size="sm"
								color="brand">
								{{
									setup.self.isAgency()
										? setup.discussionService.getDiscussionAgencyCompanyName(discussion.id)
										: setup.discussionService.getDiscussionClientCompanyName(discussion.id)
								}}
							</o-label>
							<o-label
								v-for="(interlocutor, index) in discussion.interlocutors"
								:key="index"
								outline
								size="sm">
								{{ interlocutor.name }}
							</o-label>
						</div>
					</template>
				</o-chat-discussion-list>

				<o-loader
					:visible="setup.discussionService.showLoader"
					message="Chargement des discussions..."/>
			</o-card>

			<o-card
				v-for="discussion in setup.discussionService.openedDiscussions"
				:key="discussion.id"
				class="discussion-card"
				:class="[
					setup.discussionService.minimizedDiscussions.includes(discussion.id) ? 'discussion-card--minimized' : 'discussion-card--opened',
					{ 'discussion-card--unread': discussion.unreadMessagesCount },
				]">
				<div
					v-if="setup.discussionService.minimizedDiscussions.includes(discussion.id)"
					class="discussion-card__expander">
					<o-avatar
						contain
						v-bind="discussion.interlocutors.first()"/>
					<span
						class="discussion-card__interlocutor orion-chat__interlocutors orion-chat__title"
						:data-unread-count="discussion.unreadMessagesCount">
						{{ discussion.title }}
					</span>
					<div class="discussion-card__header-actions">
						<o-icon
							v-if="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)"
							v-tooltip="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)?.label"
							ripple="default"
							font-icon="icon-interface-content-file"
							@click="setup.discussionService.goToContextAsync(discussion.id)"/>
						<o-icon
							ripple="default"
							font-icon="icon-interface-arrows-button-up"
							@click="setup.discussionService.expand(discussion.id)"/>
						<span
							class="discussion-card__close"
							@click="setup.discussionService.close(discussion.id)"/>
					</div>
				</div>

				<o-chat
					v-else
					:ref="el => { if (!!el) setup._discussions.value.push(el as unknown as OrionChat) }"
					hide-search
					focus-on-open
					:chat="setup.discussionService.chat"
					:discussion-id="discussion.id">
					<template #append-discussion-actions>
						<div class="discussion-card__header-actions">
							<o-icon
								v-if="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)"
								v-tooltip="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)?.label"
								ripple="default"
								font-icon="icon-interface-content-file"
								@click="setup.discussionService.goToContextAsync(discussion.id)"/>
							<o-icon
								ripple="default"
								font-icon="icon-interface-arrows-button-down"
								@click="setup.discussionService.minimize(discussion.id)"/>
							<span
								class="discussion-card__close"
								@click="setup.discussionService.close(discussion.id)"/>
						</div>
					</template>
				</o-chat>
			</o-card>
		</div>

		<div
			v-if="setup.displayDiscussionWrapper"
			class="shared-discussions__panel-trigger-wrapper">
			<o-button
				class="shared-discussions__panel-trigger"
				prefix-font-icon="icon-mail-chat-two-bubbles-square"
				color="brand"
				size="xl"
				@click="setup.show()"/>
			<span
				v-if="setup.discussionService.unreadMessagesCount"
				class="shared-discussions__panel-trigger-unread-count">
				{{ setup.discussionService.unreadMessagesCount }}
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import SharedDiscussionsSetupService from '@/setup/shared/SharedDiscussionsSetupService';
const setup = new SharedDiscussionsSetupService();
</script>

<style scoped lang="less">
@import '@/styles/mixins.less';
@import '@/styles/variables.less';

.shared-discussions {
  transition: right 0.3s ease-out, bottom 0.3s ease-out;
  will-change: right;
  position: fixed;
  bottom: 1rem;
  right: 1.5rem;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  gap: var(--space-sm);

  @media @layoutNavBottom {
    bottom: 5rem;
  }

  @media @phone {
    bottom: 1rem;
  }

  body.orion-body--footer-fixed-visible & {
    bottom: 7rem;
  }

  #app > #right-aside:not(:empty) ~ & {
    right: 20.5rem;
  }

  &__opened {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;
    gap: 1rem;

		@media @phone {
				margin-bottom: 3rem;
			}
  }

  &__list-close {
    .close(1.5rem);
  }

  &__panel-trigger {
    &-wrapper {
      position: relative;
      margin-bottom: 0.5rem;

			@media @phone {
				margin-bottom: 3.5rem;
        margin-right: -0.5rem;
			}

			body.orion-body--footer-fixed-visible & {
				@media @phone {
					display: none;
				}
			}
    }

    &-unread-count {
      .circle(1.25rem);
      .flex-align();
      position: absolute;
      top: -0.25rem;
      right: -0.25rem;
      background: var(--danger);
      color: white;
      font-weight: 600;
      line-height: 1.25rem;
    }
  }

  .discussions-list-card,
  .discussion-card--opened {
    width: 20rem;
    height: 30rem;
    overflow: hidden;

    :deep(.orion-card__body) {
      padding: 0;
      overflow: auto;
    }
  }

  .discussion-card--minimized {
    width: 20rem;
    height: 4rem;
    overflow: hidden;

    :deep(.orion-card__body) {
      padding: 0;
    }
  }

  .discussions-list-card {
    :deep(.orion-chat-discussion-list) {
      height: auto;
    }
  }

  .discussion-card {
    &__close {
      .close(1.5rem);
      margin-left: -0.5rem;
    }

    &__header-actions {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      margin-right: 0.25rem;

      :deep(.orion-icon) {
        font-size: 0.85rem;
      }
    }

    &__expander {
      display: flex;
      align-items: center;
      padding: var(--fluid-10px);
      height: 4rem;
      border-bottom: 0.0625rem solid white;

      .orion-avatar {
        width: 2.8125rem;
        height: 2.8125rem;
        flex-basis: 2.8125rem;
        flex-shrink: 0;
        flex: none;
        border-radius: 100%;
        border: 0.125rem solid white;
      }
    }

    &__interlocutor {
      margin-left: 0.5rem;
      margin-right: auto;
    }
  }
}
</style>
